import React, { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import ShadowCard from "../../components/common/ShadowCard";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import FormTextField from "../../components/formFields/FormTextField";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { BannerContentTitle, STATUSARRAY } from "../../_contstants/common";
import FormImageUpload from "../../components/formFields/FormImageUpload";
import {
  BannerDetailsImageUpload,
  createBanner,
  getBannerByID,
  updateBanner,
} from "./services/banner.services";

import { clearBannerDetails } from "./bannerSlice";
import FormRichTextField from "../../components/formFields/FormRichTextField";

const CreateBanner: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const BannerID = location.state?.BannerID;
  const BannerDetail = useAppSelector((state) => state.banner.BannerDetail);
  const loading = useAppSelector((state) => state.banner.loading);
  const [formKey, setFormKey] = React.useState(1);

  useEffect(() => {
    if (BannerID) {
      dispatch(getBannerByID(BannerID));
    }
    return () => {
      dispatch(clearBannerDetails());
    };
  }, [BannerID]);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [BannerDetail]);

  const handleSubmit = async (values: any) => {
    const formData = {
      BannerTitle: values.BannerTitle ?? "",
      BannerType: values.BannerType ?? "",
      ContentTitle: values.ContentTitle ?? "",
      SubTitle: values.SubTitle ?? "",
      Description: values.Description ?? "",
      IsActive: values.IsActive === 1,
    };
    const ImageData = new FormData();
    const BannerImage =
      values?.BannerImage && typeof values?.BannerImage !== "string"
        ? values.BannerImage[0].getRawFile()
        : "";
    const IconKey =
      values?.IconKey && typeof values?.IconKey !== "string"
        ? values.IconKey[0].getRawFile()
        : "";
    const AltTitle = values?.AltTitle ?? "";

    try {
      if (BannerID) {
        const editPayload = {
          ...formData,
          ID: BannerID,
        };

        const response = await dispatch(updateBanner(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (
            (values?.BannerImage && typeof values?.BannerImage !== "string") ||
            (values?.IconKey && typeof values?.IconKey !== "string")
          ) {
            ImageData.append("BannerImage", BannerImage);
            ImageData.append("IconKey", IconKey);
            ImageData.append("AltTitle", AltTitle);
            ImageData.append("ID", BannerID);
            const imageResponse = await dispatch(
              BannerDetailsImageUpload(ImageData)
            );
            if (imageResponse?.meta?.requestStatus === "fulfilled") {
              navigate("/banner");
            } else {
              navigate("/banner");
            }
          } else {
            navigate("/banner");
          }
        }
      } else {
        const response = await dispatch(createBanner(formData));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (
            ((values?.BannerImage && typeof values?.BannerImage !== "string") ||
              (values?.IconKey && typeof values?.IconKey !== "string")) &&
            response?.payload?.Data?.ID
          ) {
            ImageData.append("BannerImage", BannerImage);
            ImageData.append("IconKey", IconKey);
            ImageData.append("AltTitle", AltTitle);
            ImageData.append("ID", response?.payload?.Data?.ID);
            const imageResponse = await dispatch(
              BannerDetailsImageUpload(ImageData)
            );
            if (imageResponse?.meta?.requestStatus === "fulfilled") {
              navigate("/banner");
            } else {
              navigate("/banner");
            }
          } else {
            navigate("/banner");
          }
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  return (
    <>
      <div>
        {loading && <LoadingPanel gridRef={gridRef} />}
        <Form
          key={formKey}
          onSubmit={handleSubmit}
          initialValues={BannerDetail}
          render={(formRenderProps: FormRenderProps) => (
            <FormElement>
              <ShadowCard style={{ padding: 12 }}>
                <GridLayout
                  style={{ marginRight: 20 }}
                  gap={{ rows: 5, cols: 10 }}
                  cols={[
                    { width: "33.33%" },
                    { width: "33.33%" },
                    { width: "33.33%" },
                  ]}
                >
                  <GridLayoutItem colSpan={3}>
                    <Typography.h4>
                      {BannerID ? "Update Banner" : "Add Banner"}
                    </Typography.h4>
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="BannerTitle"
                      label="Banner Title"
                      placeholder="Banner Title"
                      component={FormTextField}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="BannerType"
                      label="Banner Type"
                      placeholder="Banner Type"
                      component={FormTextField}
                    />
                  </GridLayoutItem>
                  {false && (
                    <GridLayoutItem>
                      <Field
                        name={`ContentTitle`}
                        label="Content Title"
                        placeholder="Content Title"
                        component={FormSelectionField}
                        options={BannerContentTitle?.map((status: any) => {
                          return {
                            value: status?.value,
                            label: status?.label,
                          };
                        })}
                      />
                    </GridLayoutItem>
                  )}
                  <GridLayoutItem>
                    <Field
                      name="SubTitle"
                      label="Sub Title"
                      placeholder="Sub Title"
                      component={FormTextField}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="AltTitle"
                      label="Alt Title"
                      placeholder="Alt Title"
                      component={FormTextField}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name={`IsActive`}
                      label="Status"
                      placeholder="i.e. Active"
                      component={FormSelectionField}
                      validator={requiredValidator}
                      astrike={true}
                      options={STATUSARRAY?.map((status: any) => {
                        return {
                          value: status?.value,
                          label: status?.label,
                        };
                      })}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={2} rowSpan={2}>
                    <Field
                      name="Description"
                      label="Description"
                      placeholder="Description"
                      component={FormRichTextField}
                      style={{ height: 150 }}
                    />
                  </GridLayoutItem>

                  <GridLayoutItem
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    <Field
                      justifyContent="start"
                      name={`BannerImage`}
                      label="Banner Image"
                      type="file"
                      accept="image/*"
                      alternateName="Banner Image"
                      component={FormImageUpload}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    <Field
                      justifyContent="start"
                      name={`IconKey`}
                      label="Icon Image"
                      type="file"
                      accept="image/*"
                      alternateName="Icon Image"
                      component={FormImageUpload}
                    />
                  </GridLayoutItem>
                  {/* <GridLayoutItem></GridLayoutItem> */}

                  <GridLayoutItem
                    colSpan={3}
                    style={{
                      marginTop: 10,
                      display: "flex",
                      justifyContent: "end",
                      alignItems: "end",
                    }}
                  >
                    <div>
                      <ButtonWithLoading
                        label={BannerID ? "Update" : "Save"}
                        type="submit"
                        disabled={!formRenderProps.allowSubmit || loading}
                        loading={loading}
                      />
                      <Button
                        type="button"
                        fillMode={"outline"}
                        themeColor={"primary"}
                        style={{ marginLeft: 10 }}
                        onClick={() => navigate("/banner")}
                      >
                        Cancel
                      </Button>
                    </div>
                  </GridLayoutItem>
                </GridLayout>
              </ShadowCard>
            </FormElement>
          )}
        />
      </div>
    </>
  );
};

export default CreateBanner;
